/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { TFunction } from 'i18next';
import {
  buildDisclaimerSpans,
  DisclaimerProps,
  DisclaimerUrls,
} from '../../utils/signupLoginUtils';
import { defaultProps, SignUpPageProps } from './types';

/**
 * Translates each disclaimer segment and tags it with the URL it opens.
 *
 */
const buildSignUpDisclaimer = (
  disclaimer: DisclaimerProps,
  t: TFunction,
  urls: DisclaimerUrls = {},
): DisclaimerProps =>
  buildDisclaimerSpans(
    disclaimer,
    {
      'Terms and conditions': t('signUpView.termsAndConditions'),
      'Terms of Use': t('signUpView.termsOfUse'),
      'Privacy Statement': t('signUpView.privacyStatement'),
      'Terms of Service': t('signUpView.termsOfService'),
      'Privacy Policy': t('signUpView.privacyPolicy'),
    },
    urls,
  );

/** Resolves SignUpView's presentational props: host props win, else translated defaults. */
export const buildSignUpViewProps = (
  props: SignUpPageProps,
  t: TFunction,
  urls: DisclaimerUrls = {},
) => {
  const headings = props.headings ?? {
    ...(defaultProps.headings ?? {}),
    greeting: {
      ...(defaultProps.headings?.greeting ?? {}),
      text: t('signUpView.greeting'),
    },
    prompt: {
      ...(defaultProps.headings?.prompt ?? {}),
      text: t('signUpView.prompt'),
    },
    action: {
      ...(defaultProps.headings?.action ?? {}),
      text: t('signUpView.action'),
    },
    dividerText: {
      ...(defaultProps.headings?.dividerText ?? {}),
      text: t('signUpView.dividerText'),
    },
  };

  const emailFieldProps = props.emailFieldProps ?? {
    ...defaultProps.emailFieldProps,
    label: t('signUpView.emailLabel'),
  };

  const passwordFieldProps = props.passwordFieldProps ?? {
    ...defaultProps.passwordFieldProps,
    label: t('signUpView.passwordLabel'),
  };

  const primaryButtonProps = props.primaryButtonProps ?? {
    ...defaultProps.primaryButtonProps,
    text: t('signUpView.signUpButton'),
  };

  const socialSignUps = {
    ...(defaultProps.socialSignUps ?? {}),
    ...props.socialSignUps,
    facebook: {
      ...(defaultProps.socialSignUps?.facebook ?? {}),
      ...props.socialSignUps?.facebook,
      label: {
        ...(defaultProps.socialSignUps?.facebook?.label ?? {}),
        text: 'Facebook',
        ...props.socialSignUps?.facebook?.label,
      },
    },
    google: {
      ...(defaultProps.socialSignUps?.google ?? {}),
      ...props.socialSignUps?.google,
      label: {
        ...(defaultProps.socialSignUps?.google?.label ?? {}),
        text: 'Google',
        ...props.socialSignUps?.google?.label,
      },
    },
    apple: {
      ...(defaultProps.socialSignUps?.apple ?? {}),
      ...props.socialSignUps?.apple,
      label: {
        ...(defaultProps.socialSignUps?.apple?.label ?? {}),
        text: 'Apple',
        ...props.socialSignUps?.apple?.label,
      },
    },
    wechat: {
      ...(defaultProps.socialSignUps?.wechat ?? {}),
      ...props.socialSignUps?.wechat,
      label: {
        ...(defaultProps.socialSignUps?.wechat?.label ?? {}),
        text: 'WeChat',
        ...props.socialSignUps?.wechat?.label,
      },
    },
  };

  const disclaimer = buildSignUpDisclaimer(
    {
      ...(props.disclaimer ?? defaultProps.disclaimer ?? { text: '' }),
      text: t('signUpView.disclaimer'),
    },
    t,
    urls,
  );

  return {
    headings,
    emailFieldProps,
    passwordFieldProps,
    primaryButtonProps,
    socialSignUps,
    disclaimer,
  };
};
